import {
  type DependentProfile as DependentProfileProto,
  PersonalInfo as PersonalInfoProto,
  type PersonalProfile as PersonalProfileProto,
  type SpousalProfile as SpousalProfileProto,
} from '@taxfyle/api-internal/internal/individual_profile_pb'
import { Id } from '@/infra/id'
import { safeTimestampToDate } from '@/utils/grpc-util'
import { parseAmericanDate, dateFormatters } from '@/utils/date-util'
import { ContactInfo } from '@/features/jobs/info-gathering/ContactInfo'
import { mapContactInfoProtoToProps } from '@/features/jobs/info-gathering/contact-info-mappers'
import { PersonalInfo } from './PersonalInfo'
import type { PersonalProfileVersionProps } from './PersonalProfileVersion'
import type { SpousalProfileVersionProps } from './SpousalProfileVersion'
import type { DependentProfileVersionProps } from './DependentProfileVersion'

export function mapPersonalProfileVersionProtoToProps(
  proto: PersonalProfileProto,
): PersonalProfileVersionProps {
  const personalInfoProto = proto.getPersonalInfo()
  const contactInfoProto = proto.getContactInfo()

  return {
    id: Id(proto.getId()),
    version: proto.getVersion(),
    workspaceId: Id(proto.getWorkspaceId()),
    ownerUserId: Id(proto.getOwnerUserId()),
    occupation: proto.getOccupation(),
    createdAt: safeTimestampToDate(proto.getCreateTime()),
    updatedAt: safeTimestampToDate(proto.getUpdateTime()),
    personalInfo: personalInfoProto
      ? mapPersonalInfoProtoToProps(personalInfoProto)
      : PersonalInfo.empty,
    contactInfo: contactInfoProto
      ? mapContactInfoProtoToProps(contactInfoProto)
      : ContactInfo.empty,
  }
}

export function mapSpousalProfileVersionProtoToProps(
  proto: SpousalProfileProto,
): SpousalProfileVersionProps {
  const personalInfoProto = proto.getPersonalInfo()
  const contactInfoProto = proto.getContactInfo()

  return {
    id: Id(proto.getId()),
    version: proto.getVersion(),
    workspaceId: Id(proto.getWorkspaceId()),
    ownerUserId: Id(proto.getOwnerUserId()),
    occupation: proto.getOccupation(),
    createdAt: safeTimestampToDate(proto.getCreateTime()),
    updatedAt: safeTimestampToDate(proto.getUpdateTime()),
    personalInfo: personalInfoProto
      ? mapPersonalInfoProtoToProps(personalInfoProto)
      : PersonalInfo.empty,
    contactInfo: contactInfoProto
      ? mapContactInfoProtoToProps(contactInfoProto)
      : ContactInfo.empty,
  }
}

export function mapDependentProfileVersionProtoToProps(
  proto: DependentProfileProto,
): DependentProfileVersionProps {
  const personalInfoProto = proto.getPersonalInfo()

  return {
    id: Id(proto.getId()),
    version: proto.getVersion(),
    workspaceId: Id(proto.getWorkspaceId()),
    ownerUserId: Id(proto.getOwnerUserId()),
    relationship: proto.getRelationship(),
    createdAt: safeTimestampToDate(proto.getCreateTime()),
    updatedAt: safeTimestampToDate(proto.getUpdateTime()),
    personalInfo: personalInfoProto
      ? mapPersonalInfoProtoToProps(personalInfoProto)
      : PersonalInfo.empty,
  }
}

export function mapPersonalInfoProtoToProps(
  proto: PersonalInfoProto,
): PersonalInfo {
  const dateOfBirth = proto.getDateOfBirth()

  return {
    firstName: proto.getFirstName(),
    middleInitial: proto.getMiddleInitial() || null,
    lastName: proto.getLastName(),
    dateOfBirth: dateOfBirth ? parseAmericanDate(dateOfBirth) : null,
    personalTaxpayerIdentificationNumber:
      proto.getPersonalTaxpayerIdentificationNumber(),
  }
}

export function mapPersonalInfoToProto(
  personalInfo: PersonalInfo,
): PersonalInfoProto {
  const proto = new PersonalInfoProto()
    .setFirstName(personalInfo.firstName)
    .setLastName(personalInfo.lastName)
    .setPersonalTaxpayerIdentificationNumber(
      personalInfo.personalTaxpayerIdentificationNumber,
    )

  if (personalInfo.middleInitial) {
    proto.setMiddleInitial(personalInfo.middleInitial)
  }

  if (personalInfo.dateOfBirth) {
    proto.setDateOfBirth(
      dateFormatters.dateAmerican.format(personalInfo.dateOfBirth),
    )
  }

  return proto
}
